import {
  Alert,
  Grid,
  ReadOnlyField,
  Stack,
  SelectField,
  FiSearch,
  FormProvider,
} from '@agrotoken-tech/ui';
import { companyActivities } from '@bcba/types';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { useTranslation } from '@bcba/react-hooks';
import { DigitalDossierGeneralInformationSerializer } from '@bcba/serializers';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { useGeneralInfoForm } from './hooks';

interface Props {
  data: DigitalDossierGeneralInformationSerializer;
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<DigitalDossierGeneralInformationSerializer, Error>
  >;
}

export const GeneralInfoForm = ({ data, refetch }: Props) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    onSubmit,
    formMethods,
    isPendingChangeRequest,
    validationMessage,
    companyFiscalId,
    name,
    companyType,
    toBeCompletedByMessage,
    showForm,
    mainActivities,
    activityStartDate,
    activityEndDate,
    rucaStatus,
    igjIdentifier,
    sisaScoring,
    sisaActivities,
    actionButtons,
  } = useGeneralInfoForm({ data, refetch });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...formMethods}>
        <Stack gap={8}>
          {isPendingChangeRequest && (
            <Alert
              title={t('explorer.changeRequest.alert.title')}
              description={validationMessage}
              variant="error"
            />
          )}
          <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
            <ReadOnlyField value={companyFiscalId} label={t('common.CUIT')} />
            <ReadOnlyField value={name} label={t('common.companyName')} />
            <ReadOnlyField
              label={t('common.companyType')}
              value={
                companyType
                  ? t(`companyTypes.${companyType}`)
                  : toBeCompletedByMessage
              }
            />
            {showForm ? (
              <SelectField
                name="mainActivities"
                label={t('common.activity')}
                placeholder={t('common.selectActivity')}
                options={companyActivities.map((a) => ({
                  label: t(`companyActivities.${a}`),
                  value: a,
                }))}
                isMulti
                leftIcon={FiSearch}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
            ) : (
              <ReadOnlyField
                value={
                  mainActivities
                    ?.map((a) => t(`companyActivities.${a}`))
                    .join(', ') ?? '-'
                }
                label={t('common.activity')}
              />
            )}
            <ReadOnlyField
              value={
                activityStartDate
                  ? formatDateToShortFormat(
                      unixTimestampToDate(activityStartDate),
                    )
                  : toBeCompletedByMessage
              }
              label={t('common.startDateOfActivity')}
            />
            <ReadOnlyField
              value={
                activityEndDate
                  ? formatDateToShortFormat(
                      unixTimestampToDate(activityEndDate),
                    )
                  : toBeCompletedByMessage
              }
              label={t('common.endDateOfActivity')}
            />
            <ReadOnlyField
              value={
                rucaStatus
                  ? t(`rucaStatus.${rucaStatus}`)
                  : toBeCompletedByMessage
              }
              label={t('common.RUCA')}
            />
            <ReadOnlyField
              value={igjIdentifier ?? toBeCompletedByMessage}
              label={t('common.IGJ')}
            />
            <ReadOnlyField
              value={
                sisaActivities
                  ?.map((a) => t(`sisaActivities.${a}`))
                  .join(', ') ?? toBeCompletedByMessage
              }
              label={t('common.sisaActivity')}
            />
            <ReadOnlyField
              value={
                sisaScoring
                  ? t(`sisaScoring.${sisaScoring}`)
                  : toBeCompletedByMessage
              }
              label={t('common.sisaScoring')}
            />
          </Grid>
          {actionButtons}
        </Stack>
      </FormProvider>
    </form>
  );
};
